// src/components/users/UserFormModal.tsx
// FIXED: Removed entire certifications section from UI

import React, { useState, useEffect } from 'react';
import { X, Upload, Trash2, Camera, AlertTriangle, RefreshCw } from 'lucide-react';
import { 
  User, 
  CreateUserRequest, 
  UpdateUserRequest, 
  EmploymentStatus, 
  ClothingSize
} from '../../types/user';
import { useTenantConfigurations } from '../../hooks/useTenantConfigurations';
// Import useAuth from AuthContext
import { useAuth } from '../../contexts/AuthContext';


// Helper function to convert various date formats to YYYY-MM-DD for date input
const formatDateForInput = (dateString: string): string => {
  if (!dateString) return '';
  
  // Already in YYYY-MM-DD format
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return dateString;
  }
  
  // ISO format: 2000-10-02T00:00:00Z
  if (dateString.includes('T')) {
    return dateString.split('T')[0];
  }
  
  // US format: MM/DD/YYYY or M/D/YYYY
  if (dateString.includes('/')) {
    const parts = dateString.split('/');
    if (parts.length === 3) {
      const month = parts[0].padStart(2, '0');
      const day = parts[1].padStart(2, '0');
      const year = parts[2];
      return `${year}-${month}-${day}`;
    }
  }
  
  // Try to parse as a date
  const parsed = new Date(dateString);
  if (!isNaN(parsed.getTime())) {
    return parsed.toISOString().split('T')[0];
  }
  
  return dateString;
};

interface UserFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (userData: CreateUserRequest | UpdateUserRequest) => Promise<void>;
  user?: User;
  mode: 'create' | 'edit';
  // operatingTenantId prop removed - we'll get it from context
}

const UserFormModal: React.FC<UserFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  user,
  mode
}) => {
  // Get tenantId from AuthContext
  const { tenantId } = useAuth();
  
  const [formData, setFormData] = useState<CreateUserRequest>({
    employeeNumber: '',
    badgeNumber: '',
    firstName: '',
    middleName: '',
    lastName: '',
    rank: '',
    assignment: '',
    shift: '',
    supervisor: '',
    email: '',
    phone: '',
    department: '',
    division: '',
    hireDate: '',
    employmentStatus: 'Active',
    shirtSize: 'M',
    pantsSize: 'M',
    equipmentRestrictions: '',
    certificationLevels: [], // Keep field for data structure but remove from UI
    emergencyContactName: '',
    emergencyContactPhone: '',
    notes: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [duplicateCheck, setDuplicateCheck] = useState<{
    employeeNumberExists: boolean;
    badgeNumberExists: boolean;
  }>({ employeeNumberExists: false, badgeNumberExists: false });

  // Load tenant configurations using tenantId from context
  const { configs, loading: configsLoading, error: configsError, refresh: refreshConfigs } = useTenantConfigurations(tenantId || undefined);

  useEffect(() => {
    if (user && mode === 'edit') {
      setFormData({
        employeeNumber: user.employeeNumber,
        badgeNumber: user.badgeNumber,
        firstName: user.firstName,
        middleName: user.middleName || '',
        lastName: user.lastName,
        rank: user.rank,
        assignment: user.assignment,
        shift: user.shift || '',
        supervisor: user.supervisor || '',
        email: user.email,
        phone: user.phone || '',
        department: user.department || '',
        division: user.division || '',
        hireDate: user.hireDate ? formatDateForInput(user.hireDate) : '',
        employmentStatus: user.employmentStatus,
        shirtSize: user.shirtSize,
        pantsSize: user.pantsSize,
        equipmentRestrictions: user.equipmentRestrictions || '',
        certificationLevels: user.certificationLevels || [], // Keep data but don't show in UI
        emergencyContactName: user.emergencyContactName || '',
        emergencyContactPhone: user.emergencyContactPhone || '',
        notes: user.notes || ''
      });
      setPhotoPreview(user.photoUrl || null);
    } else {
      resetForm();
    }
  }, [user, mode, isOpen]);

  const resetForm = () => {
    setFormData({
      employeeNumber: '',
      badgeNumber: '',
      firstName: '',
      middleName: '',
      lastName: '',
      rank: '',
      assignment: '',
      shift: '',
      supervisor: '',
      email: '',
      phone: '',
      department: '',
      division: '',
      hireDate: '',
      employmentStatus: 'Active',
      shirtSize: 'M',
      pantsSize: 'M',
      equipmentRestrictions: '',
      certificationLevels: [],
      emergencyContactName: '',
      emergencyContactPhone: '',
      notes: ''
    });
    setErrors({});
    setPhotoFile(null);
    setPhotoPreview(null);
    setDuplicateCheck({ employeeNumberExists: false, badgeNumberExists: false });
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handlePhotoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePhotoRemove = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
  };

  const checkDuplicatesHandler = async () => {
    // Temporarily disabled - will implement later
    console.log('Duplicate check temporarily disabled');
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.employeeNumber) newErrors.employeeNumber = 'Employee number is required';
    if (!formData.badgeNumber) newErrors.badgeNumber = 'Badge number is required';
    if (!formData.firstName) newErrors.firstName = 'First name is required';
    if (!formData.lastName) newErrors.lastName = 'Last name is required';
    if (!formData.rank) newErrors.rank = 'Rank is required';
    if (!formData.assignment) newErrors.assignment = 'Assignment is required';
    if (!formData.email) newErrors.email = 'Email is required';
    if (!formData.hireDate) newErrors.hireDate = 'Hire date is required';

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (formData.phone && !/^\+?[\d\s\-\(\)]+$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (duplicateCheck.employeeNumberExists) {
      newErrors.employeeNumber = 'Employee number already exists';
    }

    if (duplicateCheck.badgeNumberExists) {
      newErrors.badgeNumber = 'Badge number already exists';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const submitData = mode === 'edit' && user
        ? { ...formData, userId: user.userId, version: user.version } as UpdateUserRequest
        : formData;

      await onSubmit(submitData);
      
      // Upload photo if provided
      if (photoFile && mode === 'create') {
        // Note: We'll need the created user ID to upload the photo
        // This would typically be returned from the onSubmit callback
      }
      
      onClose();
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  // Show loading state while configurations are loading
 if (configsLoading && configs.ranks.length === 0) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Loading form configurations...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold">
            {mode === 'create' ? 'Add New User' : 'Edit User'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Configuration Error Warning */}
        {configsError && (
          <div className="mx-6 mt-4 bg-yellow-50 border border-yellow-200 rounded-md p-4">
            <div className="flex items-start">
              <AlertTriangle className="w-5 h-5 text-yellow-400 mr-3 mt-0.5" />
              <div className="flex-1">
                <h4 className="text-sm font-medium text-yellow-800">Configuration Warning</h4>
                <p className="text-sm text-yellow-700 mt-1">{configsError}</p>
              </div>
              <button
                onClick={refreshConfigs}
                className="ml-3 inline-flex items-center px-3 py-1 border border-yellow-300 text-xs font-medium rounded text-yellow-800 bg-yellow-100 hover:bg-yellow-200"
              >
                <RefreshCw className="w-3 h-3 mr-1" />
                Retry
              </button>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Photo Upload Section */}
          <div className="flex items-center space-x-6">
            <div className="w-20 h-20 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center">
              {photoPreview ? (
                <img src={photoPreview} alt="User photo" className="w-full h-full object-cover" />
              ) : (
                <Camera className="w-8 h-8 text-gray-400" />
              )}
            </div>
            <div className="flex space-x-3">
              <label className="cursor-pointer bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 flex items-center space-x-2">
                <Upload className="w-4 h-4" />
                <span>Upload Photo</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  className="hidden"
                />
              </label>
              {photoPreview && (
                <button
                  type="button"
                  onClick={handlePhotoRemove}
                  className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 flex items-center space-x-2"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Remove</span>
                </button>
              )}
            </div>
          </div>

          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Employee Number *
              </label>
              <input
                type="text"
                value={formData.employeeNumber}
                onChange={(e) => handleInputChange('employeeNumber', e.target.value)}
                onBlur={checkDuplicatesHandler}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.employeeNumber ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.employeeNumber && (
                <p className="text-red-500 text-sm mt-1">{errors.employeeNumber}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Badge Number *
              </label>
              <input
                type="text"
                value={formData.badgeNumber}
                onChange={(e) => handleInputChange('badgeNumber', e.target.value)}
                onBlur={checkDuplicatesHandler}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.badgeNumber ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.badgeNumber && (
                <p className="text-red-500 text-sm mt-1">{errors.badgeNumber}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Employment Status *
              </label>
              <select
                value={formData.employmentStatus}
                onChange={(e) => handleInputChange('employmentStatus', e.target.value as EmploymentStatus)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="Terminated">Terminated</option>
                <option value="Retired">Retired</option>
                <option value="Leave of Absence">Leave of Absence</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Name *
              </label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.firstName ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.firstName && (
                <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Middle Name
              </label>
              <input
                type="text"
                value={formData.middleName}
                onChange={(e) => handleInputChange('middleName', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Name *
              </label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.lastName ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.lastName && (
                <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email *
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.email ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.email && (
                <p className="text-red-500 text-sm mt-1">{errors.email}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.phone ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.phone && (
                <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hire Date *
              </label>
              <input
                type="date"
                value={formData.hireDate}
                onChange={(e) => handleInputChange('hireDate', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.hireDate ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.hireDate && (
                <p className="text-red-500 text-sm mt-1">{errors.hireDate}</p>
              )}
            </div>
          </div>

          {/* Work Information */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Work Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rank *
                </label>
                <select
                  value={formData.rank}
                  onChange={(e) => handleInputChange('rank', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.rank ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Select Rank</option>
                  {configs.ranks.map(rank => (
                    <option key={rank} value={rank}>{rank}</option>
                  ))}
                </select>
                {errors.rank && (
                  <p className="text-red-500 text-sm mt-1">{errors.rank}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Assignment *
                </label>
                <select
                  value={formData.assignment}
                  onChange={(e) => handleInputChange('assignment', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.assignment ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Select Assignment</option>
                  {configs.assignments.map(assignment => (
                    <option key={assignment} value={assignment}>{assignment}</option>
                  ))}
                </select>
                {errors.assignment && (
                  <p className="text-red-500 text-sm mt-1">{errors.assignment}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Department
                </label>
                <select
                  value={formData.department}
                  onChange={(e) => handleInputChange('department', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Department</option>
                  {configs.departments.map(dept => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Division
                </label>
                <input
                  type="text"
                  value={formData.division}
                  onChange={(e) => handleInputChange('division', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Shift
                </label>
                <select
                  value={formData.shift}
                  onChange={(e) => handleInputChange('shift', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Shift</option>
                  {configs.shifts.map(shift => (
                    <option key={shift} value={shift}>{shift}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Supervisor
                </label>
                <input
                  type="text"
                  value={formData.supervisor}
                  onChange={(e) => handleInputChange('supervisor', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Sizing Information */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Sizing Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Shirt Size *
                </label>
                <select
                  value={formData.shirtSize}
                  onChange={(e) => handleInputChange('shirtSize', e.target.value as ClothingSize)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="XS">XS</option>
                  <option value="S">S</option>
                  <option value="M">M</option>
                  <option value="L">L</option>
                  <option value="XL">XL</option>
                  <option value="XXL">XXL</option>
                  <option value="XXXL">XXXL</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pants Size *
                </label>
                <select
                  value={formData.pantsSize}
                  onChange={(e) => handleInputChange('pantsSize', e.target.value as ClothingSize)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="XS">XS</option>
                  <option value="S">S</option>
                  <option value="M">M</option>
                  <option value="L">L</option>
                  <option value="XL">XL</option>
                  <option value="XXL">XXL</option>
                  <option value="XXXL">XXXL</option>
                </select>
              </div>
            </div>
          </div>

          {/* REMOVED: Entire Certifications Section */}

          {/* Emergency Contact */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Emergency Contact</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Emergency Contact Name
                </label>
                <input
                  type="text"
                  value={formData.emergencyContactName}
                  onChange={(e) => handleInputChange('emergencyContactName', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Emergency Contact Phone
                </label>
                <input
                  type="tel"
                  value={formData.emergencyContactPhone}
                  onChange={(e) => handleInputChange('emergencyContactPhone', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Additional Information</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Equipment Restrictions
                </label>
                <textarea
                  value={formData.equipmentRestrictions}
                  onChange={(e) => handleInputChange('equipmentRestrictions', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Any medical or physical limitations affecting gear assignment..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Additional notes or comments..."
                />
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="border-t pt-6 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className={`px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed ${
                isLoading ? 'flex items-center space-x-2' : ''
              }`}
            >
              {isLoading && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
              <span>{mode === 'create' ? 'Create User' : 'Update User'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserFormModal;
